import { useEffect, useMemo, useState, useCallback } from "react";
import { api, HERO_IMG, formatDate } from "@/lib/api";
import Header from "@/components/Header";
import StatsBar from "@/components/StatsBar";
import FiltersBar from "@/components/FiltersBar";
import VinylGrid from "@/components/VinylGrid";
import AlertsBar from "@/components/AlertsBar";
import VinylDialog from "@/components/VinylDialog";
import VinylDetailDialog from "@/components/VinylDetailDialog";
import WishlistGrid from "@/components/WishlistGrid";
import WishlistDialog from "@/components/WishlistDialog";
import WishlistDetailDialog from "@/components/WishlistDetailDialog";
import { toast } from "sonner";

export default function CollectionPage() {
    const [tab, setTab] = useState("collection");

    // Collection state
    const [vinyls, setVinyls] = useState([]);
    const [stats, setStats] = useState({ total: 0, ruth: 0, andy: 0, archive: 0, mint_or_near_mint: 0, wishlist: 0, total_value: 0 });
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({
        location: "all",
        media_condition: "all",
        speed: "all",
        genre: "all",
        availability: "all",
    });
    const [sort, setSort] = useState({ field: "created_at", order: "desc" });

    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detailRecord, setDetailRecord] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [alertFilter, setAlertFilter] = useState(null);

    // Wishlist state
    const [wishlist, setWishlist] = useState([]);
    const [wishlistLoading, setWishlistLoading] = useState(true);
    const [wlFormOpen, setWlFormOpen] = useState(false);
    const [wlEditing, setWlEditing] = useState(null);
    const [wlDetail, setWlDetail] = useState(null);

    const fetchVinyls = useCallback(async () => {
        try {
            const params = { search, sort: sort.field, order: sort.order, ...filters };
            const { data } = await api.get("/vinyls", { params });
            setVinyls(data);
        } catch (e) {
            toast.error("Failed to load collection");
        }
    }, [search, sort, filters]);

    const fetchStats = useCallback(async () => {
        try {
            const { data } = await api.get("/vinyls/stats");
            setStats(data);
        } catch (e) { /* ignore */ }
    }, []);

    const fetchWishlist = useCallback(async () => {
        try {
            const { data } = await api.get("/wishlist");
            setWishlist(data);
        } catch (e) {
            toast.error("Failed to load wishlist");
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await Promise.all([fetchVinyls(), fetchStats()]);
            setLoading(false);
        })();
    }, [fetchVinyls, fetchStats]);

    useEffect(() => {
        (async () => {
            setWishlistLoading(true);
            await fetchWishlist();
            setWishlistLoading(false);
        })();
    }, [fetchWishlist]);

    useEffect(() => {
        api.get("/vinyls/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
    }, [vinyls]);

    const alertFlags = useMemo(() => ({
        cleaning: new Set(alerts?.cleaning_due || []),
        price: Object.fromEntries((alerts?.price_rises || []).map(p => [p.id, p])),
    }), [alerts]);

    useEffect(() => {
        if (alertFilter === "cleaning" && alertFlags.cleaning.size === 0) setAlertFilter(null);
        if (alertFilter === "price" && Object.keys(alertFlags.price).length === 0) setAlertFilter(null);
    }, [alertFilter, alertFlags]);

    const shownVinyls = useMemo(() => {
        if (alertFilter === "cleaning") return vinyls.filter(v => alertFlags.cleaning.has(v.id));
        if (alertFilter === "price") return vinyls.filter(v => alertFlags.price[v.id]);
        return vinyls;
    }, [vinyls, alertFilter, alertFlags]);

    const genres = useMemo(() => {
        const set = new Set();
        vinyls.forEach(v => v.genre && set.add(v.genre));
        return Array.from(set).sort();
    }, [vinyls]);

    // Collection handlers
    const handleSave = async (payload, id) => {
        try {
            if (id) {
                await api.put(`/vinyls/${id}`, payload);
                toast.success("Record updated");
            } else {
                await api.post("/vinyls", payload);
                toast.success("Record added to collection");
            }
            setFormOpen(false);
            setEditing(null);
            await Promise.all([fetchVinyls(), fetchStats()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save record");
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/vinyls/${id}`);
            toast.success("Record removed");
            setDetailRecord(null);
            await Promise.all([fetchVinyls(), fetchStats()]);
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    const handleClean = async (id) => {
        try {
            const { data } = await api.post(`/vinyls/${id}/clean`);
            toast.success(`Marked cleaned on ${formatDate(data.date_last_cleaned)}`);
            await fetchVinyls();
            if (detailRecord?.id === id) {
                setDetailRecord({ ...detailRecord, date_last_cleaned: data.date_last_cleaned });
            }
        } catch (e) {
            toast.error("Failed to mark cleaned");
        }
    };

    const handleResync = async (id) => {
        try {
            const { data } = await api.post(`/vinyls/${id}/resync`);
            toast.success("Re-synced from Discogs");
            await fetchVinyls();
            if (detailRecord?.id === id) setDetailRecord(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Re-sync failed");
        }
    };

    const handleToggleIdp = async (id, value) => {
        try {
            const { data } = await api.put(`/vinyls/${id}`, { idp: value });
            toast.success(value ? "IDP on — insurance will use purchase price" : "IDP off — insurance will use Discogs price");
            await fetchVinyls();
            if (detailRecord?.id === id) setDetailRecord(data);
        } catch (e) {
            toast.error("Failed to update IDP");
        }
    };

    // Wishlist handlers
    const handleWlSave = async (payload, id) => {
        try {
            if (id) {
                await api.put(`/wishlist/${id}`, payload);
                toast.success("Wishlist item updated");
            } else {
                await api.post("/wishlist", payload);
                toast.success("Added to wishlist");
            }
            setWlFormOpen(false);
            setWlEditing(null);
            await Promise.all([fetchWishlist(), fetchStats()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save wishlist item");
        }
    };

    const handleWlDelete = async (id) => {
        try {
            await api.delete(`/wishlist/${id}`);
            toast.success("Removed from wishlist");
            setWlDetail(null);
            await Promise.all([fetchWishlist(), fetchStats()]);
        } catch (e) {
            toast.error("Failed to remove");
        }
    };

    const handleWlPurchase = async (id, payload) => {
        try {
            await api.post(`/wishlist/${id}/purchase`, payload);
            toast.success("Moved into collection!");
            setWlDetail(null);
            setTab("collection");
            await Promise.all([fetchWishlist(), fetchVinyls(), fetchStats()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to move");
        }
    };

    const openAdd = () => {
        if (tab === "collection") { setEditing(null); setFormOpen(true); }
        else { setWlEditing(null); setWlFormOpen(true); }
    };

    return (
        <div className="min-h-screen" data-testid="collection-page">
            <Header
                onAdd={openAdd}
                tab={tab}
                onTabChange={setTab}
                addLabel={tab === "collection" ? "Add record" : "Add wish"}
                onImported={() => Promise.all([fetchVinyls(), fetchStats()])}
            />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-border" data-testid="hero-section">
                <div className="absolute inset-0 opacity-20">
                    <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background"></div>
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                    <p className="overline text-muted-foreground mb-4">A shared archive · Ruth & Andy</p>
                    {tab === "collection" ? (
                        <>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-none tracking-tight max-w-3xl">
                                The vinyl shelf,<br />
                                <span className="text-primary italic font-medium">catalogued.</span>
                            </h1>
                            <p className="mt-6 max-w-xl text-muted-foreground leading-relaxed">
                                Every spin, sleeve and scratch, recorded. Track locations, last cleaned dates,
                                and where each record lives.
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-none tracking-tight max-w-3xl">
                                On the hunt,<br />
                                <span className="text-primary italic font-medium">and watching.</span>
                            </h1>
                            <p className="mt-6 max-w-xl text-muted-foreground leading-relaxed">
                                Records you want, target prices, vendor links. When one arrives,
                                move it into the collection with a single click.
                            </p>
                        </>
                    )}
                </div>
            </section>

            <main className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-10">
                <StatsBar
                    stats={stats}
                    activeLocation={filters.location}
                    onFilterLocation={(loc) => setFilters(f => ({ ...f, location: loc }))}
                />

                {tab === "collection" ? (
                    <>
                        <AlertsBar alerts={alerts} activeFilter={alertFilter} onFilter={setAlertFilter} />
                        <FiltersBar
                            search={search}
                            setSearch={setSearch}
                            filters={filters}
                            setFilters={setFilters}
                            sort={sort}
                            setSort={setSort}
                            genres={genres}
                        />
                        <VinylGrid
                            vinyls={shownVinyls}
                            alertFlags={alertFlags}
                            loading={loading}
                            onSelect={setDetailRecord}
                            onAdd={() => { setEditing(null); setFormOpen(true); }}
                        />
                    </>
                ) : (
                    <WishlistGrid
                        items={wishlist}
                        loading={wishlistLoading}
                        onSelect={setWlDetail}
                        onAdd={() => { setWlEditing(null); setWlFormOpen(true); }}
                    />
                )}
            </main>

            <footer className="border-t border-border mt-20 py-8">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between text-sm text-muted-foreground">
                    <p className="font-mono">{stats.total} records · {stats.wishlist || 0} wished · est. 2026</p>
                    <p className="overline">Spin · Catalog · Repeat</p>
                </div>
            </footer>

            <VinylDialog
                open={formOpen}
                onOpenChange={(o) => { setFormOpen(o); if (!o) setEditing(null); }}
                onSave={handleSave}
                initial={editing}
            />
            <VinylDetailDialog
                record={detailRecord}
                onOpenChange={(o) => { if (!o) setDetailRecord(null); }}
                onEdit={(r) => { setDetailRecord(null); setEditing(r); setFormOpen(true); }}
                onDelete={handleDelete}
                onClean={handleClean}
                onResync={handleResync}
                onToggleIdp={handleToggleIdp}
            />

            <WishlistDialog
                open={wlFormOpen}
                onOpenChange={(o) => { setWlFormOpen(o); if (!o) setWlEditing(null); }}
                onSave={handleWlSave}
                initial={wlEditing}
            />
            <WishlistDetailDialog
                item={wlDetail}
                onOpenChange={(o) => { if (!o) setWlDetail(null); }}
                onEdit={(w) => { setWlDetail(null); setWlEditing(w); setWlFormOpen(true); }}
                onDelete={handleWlDelete}
                onPurchase={handleWlPurchase}
            />
        </div>
    );
}
